// frontend/src/components/NotificationItem.tsx
// Component for displaying individual notification items

import React from 'react';
import { Button } from 'primereact/button';
import { Notification, getNotificationIcon, getNotificationSeverity, formatTimeAgo } from '../../services/notificationApi';

interface NotificationItemProps {
  notification: Notification;
  onClick?: (notification: Notification) => void;
  onAction?: (notification: Notification) => void;
  onMarkRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
  compact?: boolean;
  showActions?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onAction,
  onMarkRead,
  onDelete,
  compact = false,
  showActions = true
}) => {
  const iconClass = getNotificationIcon(notification.notification_type);
  const severity = getNotificationSeverity(notification.priority);

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkRead) {
      onMarkRead(notification.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.action_url) return;
    if (onAction) {
      onAction(notification);
      return;
    }

    const targetUrl = notification.action_url.startsWith('/')
      ? `${window.location.origin}/#${notification.action_url}`
      : notification.action_url;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  if (compact) {
    // Compact version for lists/summaries
    return (
      <div
        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
          !notification.is_read ? 'bg-blue-50' : ''
        }`}
        onClick={handleClick}
      >
        <i className={`${iconClass} text-lg ${severity === 'error' ? 'text-red-500' : severity === 'warn' ? 'text-yellow-500' : 'text-blue-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{notification.title}</div>
          <div className="text-xs text-gray-500">{formatTimeAgo(notification.created_at)}</div>
        </div>
        {!notification.is_read && (
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
        )}
      </div>
    );
  }

  // Full version for notification center
  return (
    <div
      className={`p-3 border-b cursor-pointer hover:bg-blue-50 transition-colors ${
        !notification.is_read ? 'bg-blue-25' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-full ${severity === 'error' ? 'bg-red-100' : severity === 'warn' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
          <i className={`${iconClass} ${severity === 'error' ? 'text-red-600' : severity === 'warn' ? 'text-yellow-600' : 'text-blue-600'}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <h4 className="font-medium text-sm text-gray-900 line-clamp-1">
              {notification.title}
            </h4>
            <div className="flex items-center gap-2 ml-2">
              <small className="text-gray-500 whitespace-nowrap">
                {formatTimeAgo(notification.created_at)}
              </small>
              {showActions && !notification.is_read && (
                <Button
                  icon="pi pi-check"
                  size="small"
                  text
                  className="p-1"
                  onClick={handleMarkRead}
                  tooltip="Mark as read"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              {showActions && (
                <Button
                  icon="pi pi-trash"
                  size="small"
                  text
                  className="p-1 text-red-500"
                  onClick={handleDelete}
                  tooltip="Delete notification"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
            </div>
          </div>

          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
            {notification.message}
          </p>

          {/* Action button if action_url exists */}
          {notification.action_url && (
            <Button
              label="Take Action"
              size="small"
              className="mt-2 px-3 py-1 text-xs"
              onClick={handleAction}
            />
          )}

          {/* Priority indicator for high priority notifications */}
          {(notification.priority === 'HIGH' || notification.priority === 'CRITICAL') && (
            <div className={`inline-flex items-center px-2 py-1 mt-2 text-xs font-medium rounded-full ${severity === 'error' ? 'bg-red-100 text-red-800' : severity === 'warn' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
              <i className="pi pi-exclamation-triangle mr-1"></i>
              {notification.priority === 'CRITICAL' ? 'Critical' : 'High Priority'}
            </div>
          )}
        </div>

        {/* Unread indicator */}
        {!notification.is_read && (
          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
